import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserSidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar } from "@/components/Avatar";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/utils";

const StatusBadge = ({ s }) => {
  const map = { PAID: "bg-green-500/20 text-green-400 border-green-500/30", PENDING: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", FAILED: "bg-red-500/20 text-red-400 border-red-500/30", REFUNDED: "bg-gray-500/20 text-gray-300 border-gray-500/30", CANCELLED: "bg-gray-500/20 text-gray-400 border-gray-500/30" };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${map[s] || ""}`}>{s}</span>;
};

export function MyPayments() {
  const [items, setItems] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const load = async () => { try { const { data } = await api.get("/payments"); setItems(data); } catch (e) { toast.error(formatError(e)); } };
  useEffect(() => { load(); }, []);
  const del = async () => {
    try { await api.delete(`/payments/${confirmDel.id}`); toast.success("Uplata obrisana"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen"><UserSidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-10">
        <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-white mb-6 sm:mb-8">Moja plaćanja</h1>
        <div className="card-dark overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-white/10">
              <tr><th className="p-4">Datum</th><th className="p-4">Iznos</th><th className="p-4">Opis</th><th className="p-4">Status</th><th className="p-4">Stripe ID</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-white/50">Još nema uplata.</td></tr>
              ) : items.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`pay-row-${p.id}`}>
                  <td className="p-4 text-white/70 text-sm">{new Date(p.created_at).toLocaleString("hr-HR")}</td>
                  <td className="p-4 text-white font-semibold">{formatMoney(p.amount)} {p.currency}</td>
                  <td className="p-4 text-white/60">{p.description}</td>
                  <td className="p-4"><StatusBadge s={p.status} /></td>
                  <td className="p-4 text-white/40 text-xs font-mono truncate max-w-[200px]">{p.stripe_checkout_session_id}</td>
                  <td className="p-4">
                    <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:bg-red-500/10" onClick={() => setConfirmDel(p)} data-testid={`del-my-pay-${p.id}`}><Trash2 size={14}/></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
          <AlertDialogContent className="bg-[#171717] border-white/10 text-white">
            <AlertDialogHeader><AlertDialogTitle>Obriši uplatu?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">Uplata će biti uklonjena iz vašeg pregleda. Stripe zapis ostaje netaknut.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white">Otkaži</AlertDialogCancel>
              <AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600" data-testid="confirm-del-my-pay">Obriši</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}

export function NewPayment() {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientId, setRecipientId] = useState("PLATFORM");

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/users/recipients"); setRecipients(data); } catch { /* ignore */ }
  })(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { amount: parseFloat(amount), description: desc, origin_url: window.location.origin };
      if (recipientId !== "PLATFORM") payload.recipient_id = recipientId;
      const { data } = await api.post("/payments/new", payload);
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatError(err)); setBusy(false); }
  };

  const selected = recipients.find((r) => r.id === recipientId);

  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen"><UserSidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-10">
        <h1 className="font-heading font-black text-4xl text-white mb-2">Nova uplata</h1>
        <p className="text-white/60 mb-8">Odaberite kome uplaćujete, unesite iznos i opis. Bit ćete preusmjereni na Stripe.</p>
        <form onSubmit={submit} className="card-dark p-8 max-w-xl space-y-5">
          <div>
            <Label className="text-white/80">Primatelj uplate</Label>
            <Select value={recipientId} onValueChange={setRecipientId}>
              <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white h-12 rounded-xl" data-testid="new-recipient"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#171717] border-white/10 text-white">
                <SelectItem value="PLATFORM">StreamPay (platforma)</SelectItem>
                {recipients.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`recipient-${r.id}`}>
                    {r.first_name} {r.last_name} (@{r.username})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected ? (
              <div className="flex items-center gap-3 mt-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid="recipient-preview">
                <Avatar user={selected} size={40} />
                <div className="min-w-0">
                  <div className="text-white text-sm font-semibold truncate">{selected.first_name} {selected.last_name}</div>
                  <div className="text-white/40 text-xs truncate">{selected.has_stripe ? "Uplata ide direktno na njegov Stripe račun" : "Uplata se knjiži u njegov novčanik u aplikaciji"}</div>
                </div>
              </div>
            ) : (
              <p className="text-white/40 text-xs mt-2">Prikazani su svi registrirani korisnici aplikacije.</p>
            )}
          </div>
          <div>
            <Label className="text-white/80">Iznos (EUR)</Label>
            <Input type="number" step="0.01" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required className="mt-2 bg-white/5 border-white/10 text-white h-12 rounded-xl" data-testid="new-amount" />
          </div>
          <div>
            <Label className="text-white/80">Opis plaćanja</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} required rows={3} className="mt-2 bg-white/5 border-white/10 text-white rounded-xl" data-testid="new-desc" />
          </div>
          <Button type="submit" disabled={busy} className="btn-yellow w-full h-12 rounded-xl" data-testid="new-submit">
            {busy ? "Prosljeđujemo…" : "NASTAVI NA PLAĆANJE"}
          </Button>
        </form>
      </main>
    </div>
  );
}

export function Profile() {
  const { user, refreshUser } = useAuth();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", username: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setF({ first_name: user.first_name, last_name: user.last_name, email: user.email, username: user.username }); }, [user]);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/users/me", f); await refreshUser(); toast.success("Profil ažuriran"); }
    catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };
  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); await refreshUser(); toast.success("Slika profila ažurirana"); }
    catch (err) { toast.error(formatError(err)); }
  };
  const removeAvatar = async () => {
    try { await api.delete("/users/me/avatar"); await refreshUser(); toast.success("Slika uklonjena"); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen"><UserSidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-10">
        <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-white mb-6 sm:mb-8">Profil</h1>
        <div className="card-dark p-5 sm:p-8 max-w-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-white/10">
            <Avatar user={user} size={80} className="sm:!w-24 sm:!h-24 self-start" />
            <div className="flex-1">
              <div className="font-heading font-bold text-xl text-white">{user?.first_name} {user?.last_name}</div>
              <div className="text-white/50 text-sm">{user?.email}</div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <label className="cursor-pointer" data-testid="avatar-upload-label">
                  <input type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="avatar-upload"/>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold btn-yellow"><Camera size={14}/>Promijeni sliku</span>
                </label>
                {user?.avatar_url && <Button variant="ghost" size="sm" onClick={removeAvatar} className="rounded-full text-red-400 hover:bg-red-500/10" data-testid="avatar-remove"><Trash2 size={14} className="mr-1"/>Ukloni</Button>}
              </div>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Ime</Label><Input value={f.first_name} onChange={upd("first_name")} className="mt-2 bg-white/5 border-white/10 text-white h-11 rounded-xl" data-testid="prof-first" /></div>
              <div><Label className="text-white/80">Prezime</Label><Input value={f.last_name} onChange={upd("last_name")} className="mt-2 bg-white/5 border-white/10 text-white h-11 rounded-xl" data-testid="prof-last" /></div>
            </div>
            <div><Label className="text-white/80">Username</Label><Input value={f.username} onChange={upd("username")} className="mt-2 bg-white/5 border-white/10 text-white h-11 rounded-xl" data-testid="prof-user" /></div>
            <div><Label className="text-white/80">Email</Label><Input type="email" value={f.email} onChange={upd("email")} className="mt-2 bg-white/5 border-white/10 text-white h-11 rounded-xl" data-testid="prof-email" /></div>
            <Button type="submit" disabled={busy} className="btn-yellow rounded-full px-8" data-testid="prof-save">Spremi</Button>
          </form>
        </div>
      </main>
    </div>
  );
}

export function UserSettings() {
  return null;
}
